import {
  Autocomplete,
  AutocompleteSlots,
  Box,
  FormControl,
  FormHelperText,
  useTheme,
} from '@mui/joy';
import { AutocompleteInputChangeReason, FilterOptionsState, SxProps } from '@mui/material';
import { CSSProperties, KeyboardEvent, SyntheticEvent } from 'react';
import { Control, Controller, FieldError, FieldValues, RefCallBack } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormLabel } from 'core/elements';
import { IAutocompleteOption } from 'core/types';
import { toFieldFocusRef } from 'core/utils/fieldFocus';
import { Size } from 'theme/enums';

import { popupIndicatorStyle } from './style';
import { getOptions } from './utils';

interface IProps {
  name: string;
  placeholder?: string;
  label?: string;
  extraLabel?: JSX.Element;
  tooltip?: string;
  required?: boolean;
  control?: Control<FieldValues, object>;
  error?: FieldError;
  value?: IAutocompleteOption;
  inputValue: string;
  // When defined, MUI's input becomes controlled (used to wipe the text after each pick). Left
  // undefined for the default uncontrolled behaviour.
  controlledInputValue?: string;
  options: IAutocompleteOption[];
  loading: boolean;
  getOptionKey: (option: IAutocompleteOption) => string;
  getOptionLabel: (option: IAutocompleteOption) => string;
  isOptionEqualToValue: (option: IAutocompleteOption, value: IAutocompleteOption) => boolean;
  handleInputChange: (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  renderOptions: (props, option: IAutocompleteOption, inputValue: string) => JSX.Element;
  endDecorator?: JSX.Element;
  onEndDecoratorClick?: (value: string) => void;
  handleOnChange: (_: SyntheticEvent, option?: IAutocompleteOption) => void;
  handleOnKeyDown: (event: SyntheticEvent, search: string) => void;
  handleOnFocus?: (event: SyntheticEvent) => void;
  handleOnBlur?: () => void;
  handleFilterOptions?: (
    options: IAutocompleteOption[],
    state: FilterOptionsState<IAutocompleteOption>
  ) => IAutocompleteOption[];
  size?: Size;
  hasCreateOption?: boolean;
  slots?: AutocompleteSlots;
  disabled?: boolean;
  autoFocus?: boolean;
  disableClearable?: boolean;
  inputStyle?: SxProps;
  listboxStyle?: SxProps;
  autocompleteStyle?: SxProps;
  endDecoratorStyle?: SxProps;
  wrapperStyle?: SxProps;
  showPopupIndicator?: boolean;
  multiple?: boolean;
  minCharsToSearch?: number;
  limitTags?: number;
  groupBy?: (option: IAutocompleteOption) => string;
  renderGroup?: (params) => JSX.Element;
  getOptionDisabled?: (option: IAutocompleteOption) => boolean;
  helperTexts?: string[] | JSX.Element[];
  helperTextStyle?: CSSProperties;
  maxLength?: number;
}

const AutocompleteView = ({
  name,
  placeholder = '',
  label = '',
  extraLabel = null,
  tooltip,
  required = false,
  control,
  error,
  value = null,
  inputValue,
  controlledInputValue,
  options = [],
  loading,
  getOptionKey,
  getOptionLabel,
  isOptionEqualToValue,
  handleInputChange,
  renderOptions,
  endDecorator,
  onEndDecoratorClick,
  handleOnChange,
  handleOnKeyDown,
  handleOnFocus,
  handleOnBlur,
  handleFilterOptions,
  size,
  hasCreateOption = false,
  slots,
  disabled = false,
  autoFocus = false,
  disableClearable = false,
  inputStyle = {},
  listboxStyle = {},
  autocompleteStyle = {},
  endDecoratorStyle = {},
  wrapperStyle = {},
  showPopupIndicator = true,
  multiple = false,
  minCharsToSearch = 0,
  limitTags,
  groupBy,
  renderGroup,
  getOptionDisabled,
  helperTexts = [],
  helperTextStyle = {},
  maxLength,
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const defaultEndDecoratorStyle: SxProps = {
    color: theme.palette.neutral[700],
    fontSize: 16,
  };

  const content = (field = null, fieldRef?: RefCallBack) => (
    <FormControl error={Boolean(error)} size={size} sx={wrapperStyle}>
      {label && (
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <FormLabel required={required} tooltip={tooltip}>
            {label}
          </FormLabel>

          {extraLabel}
        </Box>
      )}
      <Autocomplete
        name={name}
        {...field}
        {...(controlledInputValue !== undefined && { inputValue: controlledInputValue })}
        size={size}
        noOptionsText={
          inputValue.length < minCharsToSearch && options.length <= 0
            ? t('messageSearchByCharsLength', { length: minCharsToSearch })
            : t('messageNoOptions')
        }
        value={field?.value || value}
        options={getOptions(options, loading)}
        loading={loading}
        placeholder={placeholder}
        endDecorator={endDecorator}
        getOptionKey={getOptionKey}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        onInputChange={handleInputChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={(_, option: IAutocompleteOption) => {
          if (option?.isLoading) {
            return;
          }

          field?.onChange(option);

          if (handleOnChange) {
            handleOnChange(_, option);
          }
        }}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
          handleOnKeyDown && handleOnKeyDown(event, event?.target?.value)
        }
        slots={slots}
        slotProps={{
          root: {
            width: '100%',
          },
          input: {
            sx: inputStyle,
            maxLength,
            ...(fieldRef && { ref: toFieldFocusRef(fieldRef) }),
          },
          listbox: {
            placement: 'bottom-start',
            sx: listboxStyle,
          },
          popupIndicator: {
            sx: popupIndicatorStyle(showPopupIndicator),
          },
          endDecorator: {
            onClick: () => onEndDecoratorClick && onEndDecoratorClick(inputValue),
            sx: {
              ...defaultEndDecoratorStyle,
              ...endDecoratorStyle,
            },
          },
        }}
        filterOptions={handleFilterOptions}
        renderOption={(props, option, { inputValue }) => renderOptions(props, option, inputValue)}
        groupBy={options.length > 0 ? groupBy : undefined}
        renderGroup={options.length > 0 ? renderGroup : undefined}
        getOptionDisabled={getOptionDisabled}
        disabled={disabled}
        autoFocus={autoFocus}
        disableClearable={disableClearable}
        sx={autocompleteStyle}
        freeSolo={inputValue.length > 0 && hasCreateOption}
        multiple={multiple}
        limitTags={limitTags}
        selectOnFocus
        clearOnBlur
        forcePopupIcon
        autoHighlight
      />
      {helperTexts?.map((helperText: string | JSX.Element, index: number) => (
        <FormHelperText key={index} sx={{ fontWeight: 500, ...helperTextStyle }}>
          {helperText}
        </FormHelperText>
      ))}
    </FormControl>
  );

  if (control) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { ref, ...field } }) => content(field, ref)}
      />
    );
  }

  return content();
};

export default AutocompleteView;
